// ============================================================================
// Who cleans this suite — including the crew that has not started yet.
//
// ⛔ Ben's ruling, 2026-09-17. The suite drawer asked the database for this
// building's service lines filtered to `Active`, so a building whose sub is
// recorded, agreed and named — but whose lines start in a fortnight — showed
// an em dash where the crew goes. Houston Physicians Hospital reads that way
// today: two Scheduled lines, Yenis Rapid Clean on both, nothing in the drawer.
// An empty slot on a building that HAS an answer reads as missing data, and
// that is the same shape as the Houston service lines that went invisible the
// week before.
//
// The billing rollups keep filtering to Active and that is right — a Scheduled
// line is not earning, and the facility card already says "not counted in cost
// yet". This is not one sweep across every Active-only filter; it is one
// question ("who cleans this") answered the way the facility card already
// answers it: the name, and the date it starts.
//
// Pure function so the wording is testable without a drawer, a database or a
// timezone. ⛔ The date is formatted with fmtBareDate (TCSD-0070) — a bare
// Postgres DATE through `new Date()` renders a day early west of Greenwich,
// which is the bug that was live on the New Accounts board this same morning.
// ============================================================================

import { fmtBareDate } from '../format';

export interface CrewLine {
  provider_id: string | null;
  assignment_status: string | null;
  start_date: string | null;
  crew_size: number | null;
  service_days_per_week: number | null;
}

export interface CrewSummary {
  /** The whole sentence for the drawer row, or null when there is nothing to say. */
  text: string | null;
  /** True when every named crew is still in the future. */
  allScheduled: boolean;
}

const isActive = (s: string | null) => s === 'Active';
const isScheduled = (s: string | null) => s === 'Scheduled';

/**
 * @param lines   this building's service lines, Active and Scheduled
 * @param nameFor provider_id → provider name; unknown ids are dropped
 */
export function summariseCrew(
  lines: CrewLine[],
  nameFor: (providerId: string) => string | undefined,
): CrewSummary {
  const active = lines.filter((l) => isActive(l.assignment_status));
  const scheduled = lines.filter((l) => isScheduled(l.assignment_status));

  const named = (ls: CrewLine[]) => {
    const out = new Map<string, string>();
    for (const l of ls) {
      if (!l.provider_id) continue;
      const n = nameFor(l.provider_id);
      if (n) out.set(l.provider_id, n);
    }
    return out;
  };

  const activeByProvider = named(active);
  const scheduledByProvider = named(scheduled);
  // A provider already working here is not also "starting" here.
  for (const id of activeByProvider.keys()) scheduledByProvider.delete(id);

  // Crew size and frequency describe the work being done, so they come from a
  // running line when there is one, and from the booked line when there is not.
  const shape = active[0] ?? scheduled[0] ?? null;
  const detail =
    (shape?.crew_size ? ` · crew of ${shape.crew_size}` : '') +
    (shape?.service_days_per_week ? ` · ${shape.service_days_per_week}x/week` : '');

  const earliest = (ls: CrewLine[], ids: Set<string>) => {
    const dates = ls
      .filter((l) => l.provider_id && ids.has(l.provider_id) && l.start_date)
      .map((l) => l.start_date as string)
      .sort();
    return dates[0] ?? null;
  };

  const activeNames = [...activeByProvider.values()];
  const scheduledNames = [...scheduledByProvider.values()];

  if (!activeNames.length && !scheduledNames.length) return { text: null, allScheduled: false };

  if (!activeNames.length) {
    const from = earliest(scheduled, new Set(scheduledByProvider.keys()));
    const when = from ? fmtBareDate(from) : '';
    return {
      text: `${scheduledNames.join(', ')}${when ? ` — starts ${when}` : ' — not started yet'}${detail}`,
      allScheduled: true,
    };
  }

  let text = `${activeNames.join(', ')}${detail}`;
  if (scheduledNames.length) {
    const from = earliest(scheduled, new Set(scheduledByProvider.keys()));
    const when = from ? fmtBareDate(from) : '';
    text += ` · ${scheduledNames.join(', ')}${when ? ` from ${when}` : ' (not started yet)'}`;
  }
  return { text, allScheduled: false };
}
